/**
 * learn.4klabs.ai — public catalog with Discover / Library / Insights tabs.
 *
 * - Discover: featured hero + Netflix-style per-subject rails.
 * - Library: Gallery (grid) ⇄ Course view toggle.
 * - Insights: mock comprehension dashboard (Phase 2 preview).
 * Anonymous, no auth. Reads GET /api/learn/catalog.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Play, ChevronRight, BookOpen, GraduationCap, ArrowRight, Sparkles, Users, Zap } from 'lucide-react';
import { cn } from '../lib/utils';
import { fetchCatalog, fetchAssessmentTokens, type CatalogItem } from '../services/learnAPI';
import {
  useLearnTheme, LearnRoot, LearnTopBar, SubjectArt, subjectMeta, glassPanel, glassHero, glassChip, inkMuted, type LearnTab,
} from '../components/learn/learnShared';
import { ExperienceCard } from '../components/learn/ExperienceCard';
import { LearnAccountMenu } from '../components/learn/LearnAccountMenu';
import { useLearnerProgress } from '../components/learn/useLearnerProgress';
import { useLearnerAccess } from '../components/learn/useLearnerAccess';
import type { LessonProgress } from '../services/learnerAPI';
import { CourseShelf } from '../components/learn/CourseShelf';
import { LearnProgressPanel } from '../components/learn/LearnProgressPanel';

export const Route = createFileRoute('/learn/')({
  component: LearnCatalog,
  loader: async () => {
    try { return { items: await fetchCatalog() }; }
    catch { return { items: [] as CatalogItem[] }; }
  },
});

function groupBySubject(items: CatalogItem[]): [string, CatalogItem[]][] {
  const map = new Map<string, CatalogItem[]>();
  for (const it of items) {
    const key = it.subject ?? 'Other';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(it);
  }
  return Array.from(map.entries());
}

// Fixed subject display order (Excel is out-of-domain → always last).
const SUBJECT_ORDER = ['Physics', 'Mathematics', 'Chemistry', 'Biology', 'Science'];
function subjectRank(s?: string | null): number {
  const i = SUBJECT_ORDER.indexOf(s ?? '');
  return i >= 0 ? i : s === 'Excel' ? 999 : 500;
}

function LearnCatalog() {
  const { items: allItems } = Route.useLoaderData();
  const access = useLearnerAccess();
  // An assigned learner sees their library, not the catalogue. Filtered at the
  // top so every derived list below — hero, shelf, grids, filters — inherits it
  // rather than each remembering to.
  const items = useMemo(() => allItems.filter((i) => access.visible(i.shareToken)), [allItems, access]);
  const { theme, toggle } = useLearnTheme();
  const [tab, setTab] = useState<LearnTab>('discover');
  const [libView, setLibView] = useState<'gallery' | 'course'>('gallery');
  const [assessTokens, setAssessTokens] = useState<Set<string>>(new Set());
  const { byToken: progressByToken } = useLearnerProgress();
  useEffect(() => {
    fetchAssessmentTokens().then(setAssessTokens);
  }, []);

  // Anything started but not finished, most recent first — the single most
  // useful thing to put in front of a returning learner.
  const continueItems = useMemo(
    () =>
      items
        .filter((i) => progressByToken.get(i.shareToken)?.status === 'in_progress')
        .sort(
          (a, b) =>
            new Date(progressByToken.get(b.shareToken)?.lastViewedAt ?? 0).getTime() -
            new Date(progressByToken.get(a.shareToken)?.lastViewedAt ?? 0).getTime(),
        )
        .slice(0, 4),
    [items, progressByToken],
  );

  // Excel is out-of-domain (software skills, not K-12) — keep it as a distinct
  // bottom band so the core catalog reads as a full, cohesive K-12 grid.
  const coreItems = useMemo(
    () => items.filter((i) => i.subject !== 'Excel').slice()
      .sort((a, b) => subjectRank(a.subject) - subjectRank(b.subject) || a.order - b.order),
    [items],
  );
  const excelItems = useMemo(() => items.filter((i) => i.subject === 'Excel').slice().sort((a, b) => a.order - b.order), [items]);
  const featured = useMemo(() => coreItems.find((i) => i.featured) ?? coreItems[0] ?? items[0] ?? null, [coreItems, items]);
  // Course view (Library): same subject order, Excel last.
  const bySubject = useMemo(() => groupBySubject(items).sort((a, b) => subjectRank(a[0]) - subjectRank(b[0])), [items]);

  const pageSubjects = useMemo(
    () => [...new Set(items.map((i) => i.subject).filter(Boolean))] as string[],
    [items],
  );

  return (
    <LearnRoot theme={theme} subjects={pageSubjects}>
      <LearnTopBar theme={theme} onToggle={toggle} activeTab={tab} onTab={setTab} right={<LearnAccountMenu />} />

      <main className="learn-shell mx-auto px-6 pb-20">
        {items.length === 0 ? (
          <EmptyState />
        ) : tab === 'discover' ? (
          <>
            <HomeBento
              continueItem={continueItems[0] ?? null}
              featured={featured}
              progressByToken={progressByToken}
              totalLessons={items.length}
            />

            <CourseShelf />

            <AllLessons
              items={[...coreItems, ...excelItems]}
              assessTokens={assessTokens}
              progressByToken={progressByToken}
            />
          </>
        ) : tab === 'library' ? (
          <>
            <div className="mt-8 flex items-center justify-between">
              <h2 className="font-serif text-[30px] leading-[1.14] tracking-tight">Library</h2>
              <div className={cn('flex p-[3px]', glassChip)}>
                {(['gallery', 'course'] as const).map((v) => (
                  <button key={v} onClick={() => setLibView(v)}
                    className={cn('rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors',
                      libView === v ? 'bg-[#5B2EE5] text-white shadow-[0_8px_20px_-10px_rgba(91,46,229,0.9)]' : inkMuted)}>
                    {v === 'gallery' ? 'Gallery' : 'Course view'}
                  </button>
                ))}
              </div>
            </div>
            {libView === 'gallery' ? (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((it) => <ExperienceCard key={it.id} item={it} hasTest={assessTokens.has(it.shareToken)} progress={progressByToken.get(it.shareToken)} />)}
              </div>
            ) : (
              <CourseView bySubject={bySubject} />
            )}
          </>
        ) : (
          <>
            <InsightPortalsCTA />
            <LearnProgressPanel />
          </>
        )}
      </main>
    </LearnRoot>
  );
}

/**
 * The bento — the learner's dashboard as plates of glass on the page's light.
 *
 * The big tile is their own half-finished lesson (their real next step); the
 * small tiles are their standing, all computed from the progress rows already
 * fetched. Nothing here is invented: a learner with no history gets an honest
 * "nothing yet" rather than a fabricated streak.
 */
function HomeBento({
  continueItem,
  featured,
  progressByToken,
  totalLessons,
}: {
  continueItem: CatalogItem | null;
  featured: CatalogItem | null;
  progressByToken: Map<string, LessonProgress>;
  totalLessons: number;
}) {
  const item = continueItem ?? featured;
  if (!item) return null;
  const resuming = Boolean(continueItem);
  const p = progressByToken.get(item.shareToken);
  const meta = subjectMeta(item.subject);

  const rows = [...progressByToken.values()];
  const completed = rows.filter((r) => r.status === 'completed').length;
  const checks = rows.filter((r) => r.linkedTest?.taken);
  const bestCheck = checks.reduce<number | null>(
    (best, r) => {
      const v = r.linkedTest?.bestScorePercent;
      return v != null && (best == null || v > best) ? v : best;
    },
    null,
  );

  return (
    <section className="mt-8 grid gap-4 lg:grid-cols-3">
      {/* The learner's own next step, at full size. */}
      <Link
        to="/learn/$shareToken"
        params={{ shareToken: item.shareToken }}
        className={cn(
          glassHero,
          'group relative flex flex-col justify-between overflow-hidden p-5 transition-transform duration-300 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] lg:col-span-2 lg:row-span-2',
        )}
      >
        <div className="relative aspect-[16/9] overflow-hidden rounded-[18px] bg-[#101018]">
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
          ) : (
            <SubjectArt subject={item.subject} />
          )}
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 shadow-xl transition-transform duration-300 group-hover:scale-110">
              <Play className="h-6 w-6 fill-[#191831] text-[#191831]" />
            </span>
          </span>
        </div>

        <div className="relative pt-5">
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>
            {resuming ? 'Continue where you left off' : 'Start here'}
          </p>
          <h1 className="mt-1.5 font-serif text-[30px] leading-[1.16] tracking-tight sm:text-[38px]">
            {item.title ?? 'Interactive lesson'}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] font-semibold">
            {item.grade && <span className={cn('rounded-full px-2.5 py-1', 'bg-[#191831]/[0.06] dark:bg-white/10')}>{item.grade}</span>}
            {item.subject && (
              <span className="rounded-full px-2.5 py-1" style={{ background: `${meta.color}1F`, color: meta.color }}>
                {item.subject}
              </span>
            )}
            <span className={cn('inline-flex items-center gap-1', inkMuted)}>
              <Zap className="h-3 w-3" /> interactive
            </span>
          </div>

          {resuming && p && (
            <div className="mt-4 flex items-center gap-3">
              <span className="h-[5px] flex-1 overflow-hidden rounded-full bg-[#191831]/10 dark:bg-white/15">
                <span className="block h-full rounded-full" style={{ width: `${Math.max(4, p.maxPercent)}%`, background: meta.color }} />
              </span>
              <span className="text-[12px] font-bold tabular-nums" style={{ color: meta.color }}>{p.maxPercent}%</span>
            </div>
          )}

          <span className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#5B2EE5] px-5 py-2.5 text-[14px] font-bold text-white shadow-[0_10px_28px_-12px_rgba(91,46,229,0.9)] transition-transform group-hover:scale-[1.03]">
            <Play className="h-4 w-4 fill-current" /> {resuming ? 'Resume' : 'Start the lesson'}
          </span>
        </div>
      </Link>

      {/* Standing — real counts only. */}
      <div className={cn(glassPanel, 'flex flex-col justify-between p-5')}>
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Your progress</p>
        <div>
          <p className="font-serif text-[54px] leading-[0.9] tracking-tight tabular-nums">{completed}</p>
          <p className={cn('mt-2 text-[13.5px]', inkMuted)}>
            {completed === 0
              ? `lessons finished — ${totalLessons} waiting for you`
              : `of ${totalLessons} lessons finished`}
          </p>
        </div>
        <Link to="/learn/me" className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-[#5B2EE5] hover:underline dark:text-[#BFA8FA]">
          My Learning <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Checks — the product word is "check", never "quiz". */}
      <div className={cn(glassPanel, 'flex flex-col justify-between p-5')}>
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Checks</p>
        <div>
          {checks.length === 0 ? (
            <>
              <p className="font-serif text-[30px] leading-[1.28] tracking-tight">None taken yet</p>
              <p className={cn('mt-2 text-[13.5px]', inkMuted)}>Finish a lesson that has a check to see your score here.</p>
            </>
          ) : (
            <>
              <p className="font-serif text-[54px] leading-[0.9] tracking-tight tabular-nums">{checks.length}</p>
              <p className={cn('mt-2 text-[13.5px]', inkMuted)}>
                taken{bestCheck != null ? ` · best ${bestCheck}%` : ''}
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/** Every lesson, filterable by subject — colour chips, not a dropdown. */
function AllLessons({
  items,
  assessTokens,
  progressByToken,
}: {
  items: CatalogItem[];
  assessTokens: Set<string>;
  progressByToken: Map<string, LessonProgress>;
}) {
  const [filter, setFilter] = useState<string | null>(null);
  const subjects = [...new Set(items.map((i) => i.subject).filter(Boolean))] as string[];
  const shown = filter ? items.filter((i) => i.subject === filter) : items;

  return (
    <section className="mt-14">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Browse</p>
          <h2 className="mt-1 font-serif text-[30px] leading-[1.14] tracking-tight">Every lesson</h2>
          <p className={cn('mt-1 text-sm', inkMuted)}>Lessons you do, not watch — browse outside your class any time.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(null)}
          className={cn(
            'rounded-full px-3.5 py-1.5 text-[12.5px] font-bold transition-colors',
            filter === null
              ? 'bg-[#5B2EE5] text-white shadow-[0_8px_20px_-10px_rgba(91,46,229,0.9)]'
              : cn('bg-white/70 hover:bg-white dark:bg-white/[0.08]', inkMuted),
          )}
        >
          All {items.length}
        </button>
        {subjects.map((subject) => {
          const m = subjectMeta(subject);
          const active = filter === subject;
          return (
            <button
              key={subject}
              onClick={() => setFilter(active ? null : subject)}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-[12.5px] font-bold transition-all',
                active ? 'text-white shadow-[0_8px_20px_-10px_rgba(25,24,49,0.6)]' : 'hover:brightness-95',
              )}
              style={active ? { background: m.color } : { background: `${m.color}1F`, color: m.color }}
            >
              {subject}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {shown.map((it) => (
          <ExperienceCard key={it.id} item={it} hasTest={assessTokens.has(it.shareToken)} progress={progressByToken.get(it.shareToken)} />
        ))}
      </div>
    </section>
  );
}

function CourseView({ bySubject }: { bySubject: [string, CatalogItem[]][] }) {
  return (
    <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
      {bySubject.map(([subject, list]) => {
        const { color } = subjectMeta(subject);
        return (
          <div key={subject} className="overflow-hidden rounded-2xl border border-black/10 bg-white dark:border-white/10 dark:bg-white/[0.04]">
            <div className="flex items-center gap-3 border-b border-black/10 px-5 py-4 dark:border-white/10">
              <span className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
                <BookOpen className="h-5 w-5" style={{ color }} />
              </span>
              <div>
                <h3 className="font-serif text-[15px] font-semibold">{subject}</h3>
                <div className="text-xs text-[#8A8593]">{list.length} {list.length === 1 ? 'lesson' : 'lessons'}</div>
              </div>
            </div>
            <div>
              {list.map((it, i) => (
                <Link key={it.id} to="/learn/$shareToken" params={{ shareToken: it.shareToken }}
                  className="flex items-center gap-3 border-b border-black/10 px-5 py-3.5 transition-colors last:border-0 hover:bg-black/[0.03] dark:border-white/10 dark:hover:bg-white/[0.05]">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#5B2EE5]/15 text-[11px] font-bold text-[#5B2EE5] dark:text-[#BFA8FA]">{i + 1}</span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium leading-[1.45]">{it.title ?? 'Untitled'}</span>
                    <span className="text-[11.5px] text-[#8A8593]">{it.grade ?? ''}{it.unit ? `${it.grade ? ' · ' : ''}${it.unit}` : ''}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-[#8A8593]" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InsightPortalsCTA() {
  return (
    <div className="mt-8">
      <Link to="/learn/teacher"
        className="group flex flex-wrap items-center gap-5 overflow-hidden rounded-[26px] border border-[#0E8A80]/25 bg-gradient-to-r from-[#0E8A80]/[0.10] to-[#5B2EE5]/[0.08] p-7 transition-colors hover:border-[#0E8A80]/45">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#2F8F80]/15">
          <GraduationCap className="h-7 w-7 text-[#2F8F80]" />
        </span>
        <div className="min-w-[240px] flex-1">
          <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#5B2EE5]/30 bg-[#5B2EE5]/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5B2EE5] dark:text-[#BFA8FA]">
            <Sparkles className="h-3 w-3" /> For teachers &amp; schools
          </div>
          <h3 className="font-serif text-[22px] font-medium tracking-tight">The teacher &amp; school dashboard</h3>
          <p className="mt-1 max-w-2xl text-[13.5px] text-[#56505F] dark:text-[#B2B0C0]">
            Every student on every topic, the exact misconception behind each gap, and AI suggested next best actions for each learner and the whole class.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-xl bg-[#2F8F80] px-5 py-3 text-[15px] font-semibold text-white transition-transform group-hover:translate-x-0.5">
          Open dashboard <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link to="/learn/student" className={cn(glassPanel, "group flex items-start gap-4 p-5 transition-colors hover:bg-white/[0.9] dark:hover:bg-[#181A2A]/[0.85]")}>
          <PortalCardBody icon={<Sparkles className="h-5 w-5 text-[#5B2EE5] dark:text-[#BFA8FA]" />}
            eyebrow="For students" title="My Learning" body="The same fingerprint, first-person: what you have mastered, your next moves, your pace, and what to review." />
        </Link>
        <Link to="/learn/parent" className={cn(glassPanel, "group flex items-start gap-4 p-5 transition-colors hover:bg-white/[0.9] dark:hover:bg-[#181A2A]/[0.85]")}>
          <PortalCardBody icon={<Users className="h-5 w-5 text-[#5B2EE5] dark:text-[#BFA8FA]" />}
            eyebrow="For families" title="Parent report" body="Plain-language progress, a weekly digest, and concrete 2-minute ways to help at home. Minor-safe, no jargon." />
        </Link>
      </div>
    </div>
  );
}

function PortalCardBody({ icon, eyebrow, title, body }: { icon: React.ReactNode; eyebrow: string; title: string; body: string }) {
  return (
    <>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#5B2EE5]/10">{icon}</span>
      <div className="flex-1">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#8A8593]">{eyebrow}</div>
        <h4 className="mt-0.5 flex items-center gap-1.5 font-serif text-[17px] font-medium tracking-tight">
          {title} <ArrowRight className="h-4 w-4 text-[#8A8593] transition-transform group-hover:translate-x-0.5" />
        </h4>
        <p className="mt-1 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{body}</p>
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <div className="mt-24 flex flex-col items-center justify-center text-center">
      <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#5B2EE5]/10">
        <BookOpen className="h-8 w-8 text-[#5B2EE5] dark:text-[#BFA8FA]" />
      </div>
      <h1 className="mt-6 font-serif text-2xl font-semibold">No experiences yet</h1>
      <p className="mt-2 max-w-sm text-sm text-[#8A8593]">
        Published interactive experiences will appear here once they're added to the catalog.
      </p>
    </div>
  );
}
